/**
 * AlertToast
 *
 * Snackbar layer. Mostra alertas recentes (últimos 5s) que NÃO estão em
 * BANNER_EVENTS (esses ficam no banner persistente). Auto-dismiss.
 */

import React, { useEffect, useState, useRef } from 'react';
import { IconX } from '@tabler/icons-react';
import { useChannelAlerts } from '../../contexts/ChannelAlertsContext';
import {
  SEVERITY_THEME,
  BANNER_EVENTS,
  EVENT_ICONS,
  effectiveSeverity,
} from './alertConfig';

const AUTO_DISMISS_MS = 6000;
const STACK_LIMIT = 3;

const AlertToast = () => {
  const { alerts, markRead } = useChannelAlerts();
  const [visible, setVisible] = useState([]);
  const timeoutsRef = useRef(new Map()); // alertId → timeoutId

  // Cleanup timeouts no unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    // Pega alertas novos não-banner não-lidos criados nos últimos 5s
    const cutoff = Date.now() - 5000;
    const fresh = alerts.filter(
      (a) =>
        !a.read &&
        !BANNER_EVENTS.has(a.event) &&
        a.createdAt >= cutoff &&
        !visible.some((v) => v.id === a.id) &&
        !timeoutsRef.current.has(a.id),
    );
    if (fresh.length === 0) return;

    setVisible((prev) =>
      [...fresh.slice(0, STACK_LIMIT), ...prev].slice(0, STACK_LIMIT),
    );

    // Agenda auto-dismiss com cleanup tracking
    fresh.forEach((alert) => {
      const tid = setTimeout(() => {
        setVisible((prev) => prev.filter((v) => v.id !== alert.id));
        timeoutsRef.current.delete(alert.id);
      }, AUTO_DISMISS_MS);
      timeoutsRef.current.set(alert.id, tid);
    });
  }, [alerts, visible]);

  const handleDismiss = (id) => {
    setVisible((prev) => prev.filter((v) => v.id !== id));
    const tid = timeoutsRef.current.get(id);
    if (tid) {
      clearTimeout(tid);
      timeoutsRef.current.delete(id);
    }
    markRead(id);
  };

  if (visible.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 400,
      }}
    >
      {visible.map((alert) => {
        const severity = effectiveSeverity(alert);
        const theme = SEVERITY_THEME[severity] || SEVERITY_THEME.info;
        const Icon = EVENT_ICONS[alert.event];

        return (
          <div
            key={alert.id}
            role="status"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '12px 14px',
              background: '#fff',
              border: `1px solid ${theme.border}`,
              borderLeft: `4px solid ${theme.iconColor}`,
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              animation: 'slideInRight 0.3s ease-out',
            }}
          >
            {Icon && (
              <Icon size={18} style={{ color: theme.iconColor, flexShrink: 0, marginTop: 2 }} />
            )}
            <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5, color: '#374151' }}>
              {alert.message}
            </div>
            <button
              type="button"
              onClick={() => handleDismiss(alert.id)}
              aria-label="Fechar"
              style={{
                padding: 4,
                border: 'none',
                background: 'transparent',
                color: '#9ca3af',
                cursor: 'pointer',
              }}
            >
              <IconX size={14} />
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AlertToast;
